import { DownloadOutlined, FilterOutlined, ReloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Button, Card, DatePicker, Form, Space, Table, Typography, Row, Col, Statistic } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { useState } from 'react';
import axios from '../../config/axios';
import AppLayout from '../../layouts/app.layout';
import { formatNumber, handleAPIError } from '../../utils/helpers';

const { RangePicker } = DatePicker;
const { Title } = Typography;

interface PayoutsReportData {
	retailer_id: number;
	retailer_name: string;
	vendor_id: string;
	total_payout_amount: number;
	device_count: number;
}

interface PayoutsReportTotals {
	retailer_id: string;
	retailer_name: string;
	vendor_id: string;
	total_payout_amount: number;
	device_count: number;
}

interface PayoutsReportResponse {
	retailers: PayoutsReportData[];
	totals: PayoutsReportTotals;
	count: number;
	page: number;
	limit: number;
}

interface FilterValues {
	date_range: [dayjs.Dayjs, dayjs.Dayjs] | null;
}

const PayoutsReport = () => {
	const [form] = Form.useForm<FilterValues>();
	const [paginationConfig, setPaginationConfig] = useState({
		page: 1,
		pageSize: 20,
	});
	const [filters, setFilters] = useState<FilterValues>({
		date_range: [dayjs().startOf('year'), dayjs()],
	});

	const { data, error, isLoading, refetch } = useQuery({
		queryKey: ['payouts-report', paginationConfig.page, paginationConfig.pageSize, filters],
		queryFn: async () => {
			const params = {
				page: paginationConfig.page,
				limit: paginationConfig.pageSize,
				start: filters.date_range ? filters.date_range[0].format('YYYY-MM-DD') : null,
				end: filters.date_range ? filters.date_range[1].format('YYYY-MM-DD') : null,
			};

			const response = await axios.get('/admin/reports/payouts', {
				withCredentials: true,
				params,
			});
			return response.data.results as PayoutsReportResponse;
		},
	});

	if (error) {
		handleAPIError(error);
	}

	const payoutsData: PayoutsReportData[] = data?.retailers || [];
	const totals = data?.totals;

	const handleFilter = () => {
		const values = form.getFieldsValue();
		setFilters(values);
		setPaginationConfig({ ...paginationConfig, page: 1 });
	};

	const handleReset = () => {
		form.resetFields();
		setFilters({
			date_range: [dayjs().startOf('year'), dayjs()],
		});
		setPaginationConfig({ page: 1, pageSize: 20 });
	};

	const convertToCSV = (data: PayoutsReportData[]) => {
		const headers = ['Retailer ID', 'Retailer Name', 'Vendor ID', 'Device Count', 'Total Payout Amount'];

		const csvContent = [
			headers.join(','),
			...data.map((row) => [row.retailer_id, `"${row.retailer_name}"`, row.vendor_id, row.device_count, row.total_payout_amount].join(',')),
		].join('\n');

		return csvContent;
	};

	const handleExport = () => {
		try {
			if (!payoutsData || payoutsData.length === 0) {
				console.warn('No data available to export');
				return;
			}

			const csvContent = convertToCSV(payoutsData);

			// Create blob and download
			const blob = new Blob([csvContent], {
				type: 'text/csv;charset=utf-8;',
			});

			const url = window.URL.createObjectURL(blob);
			const link = document.createElement('a');
			link.href = url;
			link.setAttribute('download', `payouts-report-${dayjs().format('YYYY-MM-DD')}.csv`);
			document.body.appendChild(link);
			link.click();
			link.remove();

			// Cleanup the URL object to prevent memory leaks
			window.URL.revokeObjectURL(url);
		} catch (error) {
			console.error('Export failed:', error);
		}
	};

	const columns: ColumnsType<PayoutsReportData> = [
		{
			title: 'Retailer ID',
			dataIndex: 'retailer_id',
			key: 'retailer_id',
			width: 120,
		},
		{
			title: 'Retailer Name',
			dataIndex: 'retailer_name',
			key: 'retailer_name',
			width: 200,
		},
		{
			title: 'Vendor ID',
			dataIndex: 'vendor_id',
			key: 'vendor_id',
			width: 120,
		},
		{
			title: 'Device Count',
			dataIndex: 'device_count',
			key: 'device_count',
			width: 120,
			align: 'center',
		},
		{
			title: 'Total Payout Amount',
			dataIndex: 'total_payout_amount',
			key: 'total_payout_amount',
			width: 180,
			align: 'right',
			render: (value: number) => formatNumber(value),
		},
	];

	return (
		<AppLayout
			title="Payouts Report"
			subtitle="Track retailer payouts and commission payments"
		>
			{/* Summary Statistics */}
			{totals && (
				<Card className="mb-6">
					<Title
						level={5}
						className="mb-4"
					>
						Payouts Summary
					</Title>
					<Row gutter={[24, 24]}>
						<Col
							xs={24}
							sm={12}
							md={8}
							lg={6}
						>
							<Statistic
								title="Total Retailers"
								value={data?.count || 0}
								valueStyle={{ color: '#1890ff' }}
							/>
						</Col>
						<Col
							xs={24}
							sm={12}
							md={8}
							lg={6}
						>
							<Statistic
								title="Total Devices"
								value={totals.device_count}
								valueStyle={{ color: '#52c41a' }}
							/>
						</Col>
						<Col
							xs={24}
							sm={12}
							md={8}
							lg={6}
						>
							<Statistic
								title="Total Payouts"
								value={totals.total_payout_amount}
								formatter={(value) => formatNumber(value as number)}
								valueStyle={{ color: '#722ed1' }}
							/>
						</Col>
						<Col
							xs={24}
							sm={12}
							md={8}
							lg={6}
						>
							<Statistic
								title="Average Payout"
								value={totals.device_count > 0 ? totals.total_payout_amount / totals.device_count : 0}
								formatter={(value) => formatNumber(value as number)}
								valueStyle={{ color: '#13c2c2' }}
							/>
						</Col>
					</Row>
				</Card>
			)}

			{/* Filters */}
			<Card className="mb-4">
				<Form
					form={form}
					layout="inline"
					initialValues={{
						date_range: [dayjs().startOf('year'), dayjs()],
					}}
					className="gap-4"
				>
					<Form.Item
						name="date_range"
						label="Date Range"
					>
						<RangePicker
							style={{ width: 300 }}
							placeholder={['Start Date', 'End Date']}
						/>
					</Form.Item>

					<Form.Item>
						<Space>
							<Button
								type="primary"
								icon={<FilterOutlined />}
								onClick={handleFilter}
							>
								Apply Filters
							</Button>
							<Button
								icon={<ReloadOutlined />}
								onClick={handleReset}
							>
								Reset
							</Button>
							<Button
								icon={<DownloadOutlined />}
								onClick={handleExport}
								type="default"
							>
								Export
							</Button>
							<Button
								icon={<ReloadOutlined />}
								onClick={() => refetch()}
								loading={isLoading}
							>
								Refresh
							</Button>
						</Space>
					</Form.Item>
				</Form>
			</Card>

			{/* Data Table */}
			<Card>
				<Table
					columns={columns}
					dataSource={payoutsData}
					loading={isLoading}
					rowKey="retailer_id"
					scroll={{ x: 800 }}
					pagination={{
						current: paginationConfig.page,
						pageSize: paginationConfig.pageSize,
						total: data?.count || 0,
						onChange: (page, pageSize) => {
							setPaginationConfig({ page, pageSize: pageSize || 20 });
						},
						showSizeChanger: true,
						showQuickJumper: true,
						showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} retailers`,
					}}
				/>
			</Card>
		</AppLayout>
	);
};

export default PayoutsReport;
